import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { Spin } from 'grab-ui';
import OrderSearchDuck from 'ducks/order-search-duck';
import { appDataSelector } from 'selectors/app-data-selector';
import { getDefaultHeaders } from 'common/utils/headers';
import {
  fetchFullData,
  fetchInitState,
  startTrackingInterval,
  stopTrackingInterval,
} from 'actions/api-actions';
import { GRAB_API_POLL_INTERVAL } from 'constants/endpoints';
import { ViewPortProvider } from 'context/view-port';
import useUserType from 'hooks/useUserType';
import MapDuck from 'ducks/map-duck';
import DriverDuck from 'ducks/driver-duck';
import TripDuck from 'ducks/trip-duck';
import Center from 'components/layout/center';
import BookingTracker from 'components/booking-tracker/booking-tracker';
import expiredOrderSVG from 'public/images/expired-order.svg';

// eslint-disable-next-line
import styles from './order-info.less';

const ORDER_SEARCH_PAGE_TRACKING_LINK_VERSION = 2;

const OrderInfoDisplay = () => {
  const selectedOrderGUID = useSelector(
    OrderSearchDuck.selectors.selectedOrderGuid,
  );
  const {
    enableWebTrackingUICashlessPOD,
    enableWebTrackingStayContactable,
  } = useSelector(appDataSelector);

  const userType = useUserType();

  const MapData = useSelector(MapDuck.selectors.map);
  const DriverData = useSelector(DriverDuck.selectors.driver);
  const TripData = useSelector(TripDuck.selectors.trip);

  return (
    <ViewPortProvider>
      <BookingTracker
        featureFlag={{
          hideAddInstruction: true,
          hideCallAndChat: true,
          hidePhoto: true,
          hideCashlessPOD: !enableWebTrackingUICashlessPOD,
          hideStayContactable: !enableWebTrackingStayContactable,
        }}
        map={MapData}
        driver={DriverData}
        trip={TripData}
        userType={userType}
        guid={selectedOrderGUID}
      />
    </ViewPortProvider>
  );
};

const OrderInfoError = () => (
  <div className={styles.message}>
    <img src={expiredOrderSVG} alt="invalid" />
    <span className={styles.infoTitle}>
      {I18n.t('orderSearch.orderInvalid.title')}
    </span>
    <span className={styles.infoDescription}>
      {I18n.t('orderSearch.orderInvalid.description')}
    </span>
  </div>
);

const OrderInfoExpired = () => (
  <div className={styles.message}>
    <img src={expiredOrderSVG} alt="expired" />
    <span className={styles.infoTitle}>
      {I18n.t('orderSearch.orderExpired.title')}
    </span>
    <span className={styles.infoDescription}>
      {I18n.t('orderSearch.orderExpired.description')}
    </span>
  </div>
);

const OrderInfo = () => {
  const dispatch = useDispatch();

  const selectedOrderGUID = useSelector(
    OrderSearchDuck.selectors.selectedOrderGuid,
  );

  const selectedOrderID = useSelector(
    OrderSearchDuck.selectors.selectedOrderID,
  );

  const isFirstMount = useRef(true);

  const {
    isLoadingInitData,
    isLoadedInitData,
    isErrorInitData,
    isErrorDynamicData,
    isErrorFullData,
    isExpiredGuid,
    trackingInterval,
  } = useSelector(appDataSelector);

  const isError = isErrorInitData || isErrorDynamicData || isErrorFullData;

  useEffect(
    () => {
      if (isFirstMount.current) isFirstMount.current = false;
      if (selectedOrderGUID) {
        dispatch(
          fetchInitState(
            selectedOrderGUID,
            { version: ORDER_SEARCH_PAGE_TRACKING_LINK_VERSION },
            getDefaultHeaders(),
          ),
        );
      }
    },
    [selectedOrderGUID],
  );

  useEffect(
    () => {
      const shouldStartInterval =
        isLoadedInitData &&
        !trackingInterval &&
        !isErrorInitData &&
        !isExpiredGuid;

      if (shouldStartInterval) {
        const intervalId = setInterval(() => {
          const defaultHeaders = getDefaultHeaders();

          dispatch(
            fetchFullData(
              selectedOrderGUID,
              { version: ORDER_SEARCH_PAGE_TRACKING_LINK_VERSION },
              defaultHeaders,
            ),
          );
        }, GRAB_API_POLL_INTERVAL);
        dispatch(startTrackingInterval({ intervalId }));
      }

      return () => {
        if (trackingInterval) {
          clearInterval(trackingInterval);
          dispatch(stopTrackingInterval());
        }
      };
    },
    [isLoadedInitData, isExpiredGuid, selectedOrderGUID],
  );

  const mapRender = useMemo(
    () => {
      if (isFirstMount.current || isLoadingInitData)
        return (
          <Center className={styles.fullHeight}>
            <Spin />
          </Center>
        );

      if (isError) {
        return <OrderInfoError />;
      }

      if (isExpiredGuid) {
        return <OrderInfoExpired />;
      }

      return <OrderInfoDisplay />;
    },
    [isLoadingInitData, isFirstMount, isError, isExpiredGuid],
  );

  if (!selectedOrderGUID) {
    return selectedOrderID ? (
      <div className={styles.container}>
        <OrderInfoError />
      </div>
    ) : null;
  }

  return <div className={styles.container}>{mapRender}</div>;
};

export default OrderInfo;
